import * as React from 'react';

// Small shared presentational pieces for the Easy Checkout flow.

export function Card({ children, title, subtitle }) {
  return (
    <section className="ec-card">
      {title && <h2 className="ec-card-title">{title}</h2>}
      {subtitle && <div className="ec-card-sub">{subtitle}</div>}
      {children}
    </section>
  );
}

// The line under a field. Always in the DOM (empty), so a screen reader that
// ignores a live region inserted already filled still hears it, and it opens and
// closes with a grid-row transition instead of shoving everything below it
// (design: [data-err-wrap]).
export function FieldError({ id, message }) {
  return (
    <div className={`ec-err-wrap ${message ? 'is-shown' : ''}`.trim()}>
      <div><div id={id} role="alert" className="ec-field-error">{message || ''}</div></div>
    </div>
  );
}

export function Button({ text, children, onClick, variant, disabled, type = 'button', className = '' }) {
  return (
    <button
      type={type}
      className={`ec-cta ${variant === 'outlined' ? 'ec-cta--outline' : ''} ${className}`.trim()}
      onClick={onClick}
      disabled={disabled}
    >
      {children || text}
    </button>
  );
}

// Boxed error banner — read live off the design's actual declined-payment
// alert (STEP 4 OF 4, triggered via its `simulate="declined"` prop): pink
// background, a red circle "i" icon, red text. This was previously a plain
// bordered box with no icon and the wrong red (#D0494A instead of the
// design's own #C5292A/#A51F20 pair) — never actually checked against the
// design, just a placeholder that happened to look plausible.
export function Alert({ children }) {
  return (
    <div role="alert" style={{
      marginTop: 20, display: 'flex', gap: 12, alignItems: 'flex-start',
      background: '#FDEDED', border: '1px solid #F2B8B8', borderRadius: 10, padding: 16,
    }}
    >
      <span aria-hidden="true" style={{ flex: '0 0 auto', width: 24, height: 24, borderRadius: 99, background: '#C5292A', color: '#FFFFFF', fontSize: 15, lineHeight: '24px', textAlign: 'center' }}>!</span>
      <span style={{ fontSize: '16.5px', lineHeight: 1.5, color: '#A51F20' }}>{children}</span>
    </div>
  );
}

// Generic modal — shared shape for the session-lost guard dialog. Prop 65
// renders its own markup directly (its "Got it" button uses a lighter style
// than this dialog's dark CTA).
export function NoticeDialog({ open, title, message, actionLabel, onAction }) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      style={{ position: 'fixed', inset: 0, background: 'rgba(35, 31, 30, 0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 450, background: '#FFF', borderRadius: 14, padding: 22, boxShadow: '0 12px 32px rgba(35, 31, 30, 0.22)' }}>
        <div style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.3, marginBottom: 10 }}>{title}</div>
        <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ec-label)', margin: 0 }}>{message}</p>
        <Button text={actionLabel} onClick={onAction} />
      </div>
    </div>
  );
}

// Page shell every step renders inside. The wordmark is a real <img> in the
// design (not text to recreate with a font), pointing at the same
// production CDN logo the rest of misstomrsbox.com uses. Header is
// `position:sticky` with its own full-bleed white background + border; only
// the inner row (482px, matching the design's own header/main max-width) is
// centered.
const LOGO_SRC = 'https://misstomrsbox.com/wp-content/uploads/tilda/4135798/pages/137449693/tild3731-3564-4034-b466-326536643966__logo_main.png';
export function StepShell({ stepLabel, children, runway }) {
  return (
    <div className="ec-app">
      <header className="ec-header">
        <div className="ec-header-inner">
          <img src={LOGO_SRC} alt="Miss To Mrs" style={{ display: 'block', height: 18.4, width: 'auto' }} />
        </div>
      </header>
      <div className={`ec-main ${runway ? 'ec-main--runway' : ''}`.trim()}>
        {stepLabel && <div className="ec-step-label">{stepLabel}</div>}
        {children}
      </div>
    </div>
  );
}
